import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton } from '@mui/material';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { AccountData, PolicyData } from '../../../api';
import { useLanguage } from '../../../utils/GeneralUtils';
import { OrderPageCheckbox } from '../../themedComponents/OrderPageCheckbox';

type TermsConditionsCheckboxProps = {
  account?: AccountData;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  touched?: boolean;
  error?: string | boolean;
};

export const TermsConditionsCheckbox = ({
  account,
  checked,
  onChange,
  touched,
  error,
}: TermsConditionsCheckboxProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const language = useLanguage();
  const accountName = account?.name;
  const terms_policy = account?.business_details?.terms_policy;
  const privacy_policy = account?.business_details?.privacy_policy;
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [policyDialogContent, setPolicyDialogContent] = useState<{ title: string; content: string } | null>(null);

  const openPolicyDialog = (policy: PolicyData, title: string) => {
    const content = policy.content?.[language as 'en' | 'he'] || policy.content?.en || '';
    setPolicyDialogContent({ title, content });
    setPolicyDialogOpen(true);
  };

  const renderPolicyLink = (policy: PolicyData | undefined, label: string, title: string) => {
    if (!policy) return null;

    if (policy.external_url) {
      return (
        <a
          style={{ color: theme.customTheme.palette.primary }}
          href={policy.external_url}
          target="_blank"
          rel="noreferrer">
          {label}
        </a>
      );
    }

    if (policy.content) {
      return (
        <span
          style={{
            color: theme.customTheme.palette.primary,
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
          onClick={() => openPolicyDialog(policy, title)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              openPolicyDialog(policy, title);
            }
          }}>
          {label}
        </span>
      );
    }

    return null;
  };

  return (
    <>
      <Grid container item mb={-1}>
        <FormControlLabel
          control={
            <OrderPageCheckbox
              id="terms_conditions_checked"
              name="terms_conditions_checked"
              checked={checked}
              onChange={onChange}
            />
          }
          label={
            <Grid container columnGap={1}>
              {t('order_page.terms.iaprove')}

              <a
                style={{ color: theme.customTheme.palette.primary }}
                href={`https://www.monkeybook.io/monkeybook-terms`}
                target="_blank"
                rel="noreferrer">
                {t('order_page.terms.webooksterms')}
              </a>

              {terms_policy && t('order_page.terms.and')}
              {renderPolicyLink(
                terms_policy,
                t('order_page.terms.accountterms', { accountName }),
                t('order_page.terms.accountterms', { accountName }),
              )}
              {privacy_policy && t('order_page.terms.and')}
              {renderPolicyLink(
                privacy_policy,
                t('order_page.terms.accountPrivacy', { accountName }),
                t('order_page.terms.accountPrivacy', { accountName }),
              )}
            </Grid>
          }
        />
      </Grid>
      {touched && error && (
        <Grid item color={theme.customTheme.palette.error}>
          <Typography fontSize="12px">{error}</Typography>
        </Grid>
      )}

      <Dialog
        fullScreen
        open={policyDialogOpen}
        onClose={() => setPolicyDialogOpen(false)}
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: theme.customTheme.palette.background,
          },
        }}>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px solid ${theme.customTheme.palette.border}`,
          }}>
          <Typography sx={{ color: theme.customTheme.palette.text.primary }}>{policyDialogContent?.title}</Typography>
          <IconButton onClick={() => setPolicyDialogOpen(false)} aria-label="close">
            <CloseIcon sx={{ color: theme.customTheme.palette.text.primary }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography
            sx={{ whiteSpace: 'pre-wrap', color: theme.customTheme.palette.text.primary }}
            dangerouslySetInnerHTML={{ __html: policyDialogContent?.content || '' }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
